import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Diamond, 
  ChevronLeft,
  ChevronRight,
  Check,
  CreditCard,
  Truck,
  MapPin,
  Lock,
  ShoppingBag
} from "lucide-react";
import { resolveImageUrl } from "../lib/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState({ items: [] });
  
  const sessionId = localStorage.getItem("vk_session_id") || "";

  // Form states
  const [shippingForm, setShippingForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: ""
  });

  const [deliveryMethod, setDeliveryMethod] = useState("standard");
  
  const [paymentForm, setPaymentForm] = useState({
    payment_method: "card",
    card_number: "",
    card_expiry: "",
    card_cvc: "",
    card_name: ""
  });

  const [errors, setErrors] = useState({});

  const deliveryOptions = [
    { id: "standard", name: "Standard Shipping", price: 25, time: "5-7 business days" },
    { id: "express", name: "Express Shipping", price: 50, time: "2-3 business days" },
    { id: "overnight", name: "Overnight Shipping", price: 100, time: "Next business day" }
  ];

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart/${sessionId}`);
      if (!res.data.items || res.data.items.length === 0) {
        toast.error("Your cart is empty");
        navigate("/shop");
        return;
      }
      setCart(res.data);
    } catch (err) {
      toast.error("Failed to load cart");
      navigate("/shop");
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Capture abandoned cart lead — debounced save once email is filled
  useEffect(() => {
    const email = shippingForm.email?.trim();
    // Only capture once email looks valid; rest of fields can be empty/in-progress
    if (!email || !email.includes("@") || !email.includes(".")) return;
    if (!sessionId) return;

    const timer = setTimeout(() => {
      axios
        .post(`${API}/abandoned-cart`, {
          session_id: sessionId,
          email,
          first_name: shippingForm.first_name || null,
          last_name: shippingForm.last_name || null,
          phone: shippingForm.phone || null,
          country: shippingForm.country || null,
        })
        .catch((err) => console.error("Lead capture failed:", err));
    }, 800);
    return () => clearTimeout(timer);
  }, [
    shippingForm.email,
    shippingForm.first_name,
    shippingForm.last_name,
    shippingForm.phone,
    shippingForm.country,
    sessionId,
  ]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };

  const subtotal = cart.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  const tax = subtotal * 0.08;
  const shippingCost = deliveryOptions.find(d => d.id === deliveryMethod)?.price || 25;
  const total = subtotal + tax + shippingCost;

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!shippingForm.first_name.trim()) newErrors.first_name = "First name is required";
    if (!shippingForm.last_name.trim()) newErrors.last_name = "Last name is required";
    if (!shippingForm.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(shippingForm.email)) newErrors.email = "Invalid email format";
    if (!shippingForm.phone.trim()) newErrors.phone = "Phone is required";
    if (!shippingForm.address_line1.trim()) newErrors.address_line1 = "Address is required";
    if (!shippingForm.city.trim()) newErrors.city = "City is required";
    if (!shippingForm.state.trim()) newErrors.state = "State is required";
    if (!shippingForm.postal_code.trim()) newErrors.postal_code = "Postal code is required";
    if (!shippingForm.country.trim()) newErrors.country = "Country is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (paymentForm.payment_method === "card") {
      if (!paymentForm.card_number.trim()) newErrors.card_number = "Card number is required";
      else if (paymentForm.card_number.replace(/\s/g, '').length < 16) newErrors.card_number = "Invalid card number";
      if (!paymentForm.card_expiry.trim()) newErrors.card_expiry = "Expiry date is required";
      if (!paymentForm.card_cvc.trim()) newErrors.card_cvc = "CVC is required";
      if (!paymentForm.card_name.trim()) newErrors.card_name = "Cardholder name is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 3 && !validateStep3()) return;
    
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const checkoutData = {
        session_id: sessionId,
        shipping_address: shippingForm,
        delivery_method: deliveryMethod,
        payment_method: paymentForm.payment_method,
        card_last_four: paymentForm.card_number.slice(-4) || null
      };

      const res = await axios.post(`${API}/checkout`, checkoutData);
      
      // Navigate to confirmation page with order data
      navigate(`/order-confirmation/${res.data.id}`, { state: { order: res.data } });
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value) => {
    return value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim().slice(0, 19);
  };

  const formatExpiry = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2').slice(0, 5);
  };

  return (
    <div className="min-h-screen bg-[#080809]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="checkout-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="checkout-logo">
              <Diamond className="w-8 h-8 text-primary" />
              <span className="text-xl font-light tracking-tight text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                VK Gems & Jewels
              </span>
            </Link>
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
              <Lock className="w-4 h-4" />
              <span>Secure Checkout</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12" data-testid="checkout-steps">
            {[
              { num: 1, label: "Shipping", icon: MapPin },
              { num: 2, label: "Delivery", icon: Truck },
              { num: 3, label: "Payment", icon: CreditCard }
            ].map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div 
                  className={`flex items-center gap-2 px-4 py-2 ${
                    step >= s.num ? "text-primary" : "text-[#71717A]"
                  }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center ${
                    step > s.num ? "bg-primary text-[#080809]" : step === s.num ? "border-2 border-primary" : "border border-white/20"
                  }`}>
                    {step > s.num ? <Check className="w-4 h-4" /> : <span className="text-sm">{s.num}</span>}
                  </div>
                  <span className="text-sm hidden sm:block">{s.label}</span>
                </div>
                {idx < 2 && (
                  <div className={`w-12 h-px ${step > s.num ? "bg-primary" : "bg-white/20"}`} />
                )}
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-2">
              {/* Step 1: Shipping */}
              {step === 1 && (
                <div className="glass-card p-8" data-testid="shipping-form">
                  <h2 className="text-2xl font-light text-[#F5F5F0] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Shipping Information
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#71717A] uppercase tracking-wider">First Name *</label>
                        <input 
                          type="text"
                          value={shippingForm.first_name}
                          onChange={(e) => setShippingForm({...shippingForm, first_name: e.target.value})}
                          className={`form-input-dark w-full ${errors.first_name ? "border-red-500" : ""}`}
                          data-testid="shipping-first-name"
                        />
                        {errors.first_name && <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>}
                      </div>
                      <div>
                        <label className="text-xs text-[#71717A] uppercase tracking-wider">Last Name *</label>
                        <input 
                          type="text"
                          value={shippingForm.last_name}
                          onChange={(e) => setShippingForm({...shippingForm, last_name: e.target.value})}
                          className={`form-input-dark w-full ${errors.last_name ? "border-red-500" : ""}`}
                          data-testid="shipping-last-name"
                        />
                        {errors.last_name && <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#71717A] uppercase tracking-wider">Email *</label>
                        <input 
                          type="email"
                          value={shippingForm.email}
                          onChange={(e) => setShippingForm({...shippingForm, email: e.target.value})}
                          className={`form-input-dark w-full ${errors.email ? "border-red-500" : ""}`}
                          data-testid="shipping-email"
                        />
                        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <label className="text-xs text-[#71717A] uppercase tracking-wider">Phone *</label>
                        <input 
                          type="tel"
                          value={shippingForm.phone}
                          onChange={(e) => setShippingForm({...shippingForm, phone: e.target.value})}
                          className={`form-input-dark w-full ${errors.phone ? "border-red-500" : ""}`}
                          data-testid="shipping-phone"
                        />
                        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-[#71717A] uppercase tracking-wider">Address Line 1 *</label>
                      <input 
                        type="text"
                        value={shippingForm.address_line1}
                        onChange={(e) => setShippingForm({...shippingForm, address_line1: e.target.value})}
                        className={`form-input-dark w-full ${errors.address_line1 ? "border-red-500" : ""}`}
                        data-testid="shipping-address1"
                      />
                      {errors.address_line1 && <p className="text-xs text-red-500 mt-1">{errors.address_line1}</p>}
                    </div>

                    <div>
                      <label className="text-xs text-[#71717A] uppercase tracking-wider">Address Line 2</label>
                      <input 
                        type="text"
                        value={shippingForm.address_line2}
                        onChange={(e) => setShippingForm({...shippingForm, address_line2: e.target.value})}
                        className="form-input-dark w-full"
                        data-testid="shipping-address2"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#71717A] uppercase tracking-wider">City *</label>
                        <input 
                          type="text"
                          value={shippingForm.city}
                          onChange={(e) => setShippingForm({...shippingForm, city: e.target.value})}
                          className={`form-input-dark w-full ${errors.city ? "border-red-500" : ""}`}
                          data-testid="shipping-city"
                        />
                        {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
                      </div>
                      <div>
                        <label className="text-xs text-[#71717A] uppercase tracking-wider">State/Province *</label>
                        <input 
                          type="text"
                          value={shippingForm.state}
                          onChange={(e) => setShippingForm({...shippingForm, state: e.target.value})}
                          className={`form-input-dark w-full ${errors.state ? "border-red-500" : ""}`}
                          data-testid="shipping-state"
                        />
                        {errors.state && <p className="text-xs text-red-500 mt-1">{errors.state}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#71717A] uppercase tracking-wider">Postal Code *</label>
                        <input 
                          type="text"
                          value={shippingForm.postal_code}
                          onChange={(e) => setShippingForm({...shippingForm, postal_code: e.target.value})}
                          className={`form-input-dark w-full ${errors.postal_code ? "border-red-500" : ""}`}
                          data-testid="shipping-postal"
                        />
                        {errors.postal_code && <p className="text-xs text-red-500 mt-1">{errors.postal_code}</p>}
                      </div>
                      <div>
                        <label className="text-xs text-[#71717A] uppercase tracking-wider">Country *</label>
                        <input 
                          type="text"
                          value={shippingForm.country}
                          onChange={(e) => setShippingForm({...shippingForm, country: e.target.value})}
                          className={`form-input-dark w-full ${errors.country ? "border-red-500" : ""}`}
                          data-testid="shipping-country"
                        />
                        {errors.country && <p className="text-xs text-red-500 mt-1">{errors.country}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Delivery */}
              {step === 2 && (
                <div className="glass-card p-8" data-testid="delivery-form">
                  <h2 className="text-2xl font-light text-[#F5F5F0] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Delivery Method
                  </h2>
                  
                  <div className="space-y-4">
                    {deliveryOptions.map((option) => (
                      <label
                        key={option.id}
                        className={`flex items-center justify-between p-6 cursor-pointer transition-all ${
                          deliveryMethod === option.id
                            ? "bg-[#121214] border-2 border-primary"
                            : "bg-[#121214] border border-white/10 hover:border-white/20"
                        }`}
                        data-testid={`delivery-${option.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-5 h-5 border-2 flex items-center justify-center ${
                            deliveryMethod === option.id ? "border-primary" : "border-white/30"
                          }`}>
                            {deliveryMethod === option.id && <div className="w-2.5 h-2.5 bg-primary" />}
                          </div>
                          <div>
                            <p className="text-[#F5F5F0] font-medium">{option.name}</p>
                            <p className="text-sm text-[#71717A]">{option.time}</p>
                          </div>
                        </div>
                        <span className="text-[#F5F5F0]">{formatPrice(option.price)}</span>
                        <input
                          type="radio"
                          name="delivery"
                          value={option.id}
                          checked={deliveryMethod === option.id}
                          onChange={(e) => setDeliveryMethod(e.target.value)}
                          className="hidden"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Payment */}
              {step === 3 && (
                <div className="glass-card p-8" data-testid="payment-form">
                  <h2 className="text-2xl font-light text-[#F5F5F0] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Payment Details
                  </h2>
                  
                  <div className="space-y-6">
                    {/* Payment Method Selection */}
                    <div className="flex gap-4 mb-6">
                      <label className={`flex-1 p-4 cursor-pointer transition-all ${
                        paymentForm.payment_method === "card"
                          ? "bg-[#121214] border-2 border-primary"
                          : "bg-[#121214] border border-white/10 hover:border-white/20"
                      }`}>
                        <div className="flex items-center gap-3">
                          <CreditCard className={`w-5 h-5 ${paymentForm.payment_method === "card" ? "text-primary" : "text-[#71717A]"}`} />
                          <span className="text-[#F5F5F0]">Credit Card</span>
                        </div>
                        <input
                          type="radio"
                          name="payment_method"
                          value="card"
                          checked={paymentForm.payment_method === "card"}
                          onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                          className="hidden"
                          data-testid="payment-card-radio"
                        />
                      </label>
                      <label className={`flex-1 p-4 cursor-pointer transition-all ${
                        paymentForm.payment_method === "paypal"
                          ? "bg-[#121214] border-2 border-primary"
                          : "bg-[#121214] border border-white/10 hover:border-white/20"
                      }`}>
                        <div className="flex items-center gap-3">
                          <span className="text-xl font-bold text-[#0070BA]">Pay</span>
                          <span className="text-xl font-bold text-[#003087]">Pal</span>
                        </div>
                        <input
                          type="radio"
                          name="payment_method"
                          value="paypal"
                          checked={paymentForm.payment_method === "paypal"}
                          onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                          className="hidden"
                          data-testid="payment-paypal-radio"
                        />
                      </label>
                    </div>

                    {paymentForm.payment_method === "card" && (
                      <>
                        <div>
                          <label className="text-xs text-[#71717A] uppercase tracking-wider">Cardholder Name *</label>
                          <input 
                            type="text"
                            value={paymentForm.card_name}
                            onChange={(e) => setPaymentForm({...paymentForm, card_name: e.target.value})}
                            placeholder="John Doe"
                            className={`form-input-dark w-full ${errors.card_name ? "border-red-500" : ""}`}
                            data-testid="card-name"
                          />
                          {errors.card_name && <p className="text-xs text-red-500 mt-1">{errors.card_name}</p>}
                        </div>

                        <div>
                          <label className="text-xs text-[#71717A] uppercase tracking-wider">Card Number *</label>
                          <input 
                            type="text"
                            value={paymentForm.card_number}
                            onChange={(e) => setPaymentForm({...paymentForm, card_number: formatCardNumber(e.target.value)})}
                            placeholder="1234 5678 9012 3456"
                            className={`form-input-dark w-full ${errors.card_number ? "border-red-500" : ""}`}
                            data-testid="card-number"
                          />
                          {errors.card_number && <p className="text-xs text-red-500 mt-1">{errors.card_number}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-[#71717A] uppercase tracking-wider">Expiry Date *</label>
                            <input 
                              type="text"
                              value={paymentForm.card_expiry}
                              onChange={(e) => setPaymentForm({...paymentForm, card_expiry: formatExpiry(e.target.value)})}
                              placeholder="MM/YY"
                              className={`form-input-dark w-full ${errors.card_expiry ? "border-red-500" : ""}`}
                              data-testid="card-expiry"
                            />
                            {errors.card_expiry && <p className="text-xs text-red-500 mt-1">{errors.card_expiry}</p>}
                          </div>
                          <div>
                            <label className="text-xs text-[#71717A] uppercase tracking-wider">CVC *</label>
                            <input 
                              type="text"
                              value={paymentForm.card_cvc}
                              onChange={(e) => setPaymentForm({...paymentForm, card_cvc: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                              placeholder="123"
                              className={`form-input-dark w-full ${errors.card_cvc ? "border-red-500" : ""}`}
                              data-testid="card-cvc"
                            />
                            {errors.card_cvc && <p className="text-xs text-red-500 mt-1">{errors.card_cvc}</p>}
                          </div>
                        </div>
                      </>
                    )}

                    {paymentForm.payment_method === "paypal" && (
                      <div className="text-center py-8 bg-[#121214] border border-white/10">
                        <p className="text-[#A1A1AA] mb-4">You will be redirected to PayPal to complete your purchase securely.</p>
                        <div className="flex justify-center gap-2">
                          <span className="text-2xl font-bold text-[#0070BA]">Pay</span>
                          <span className="text-2xl font-bold text-[#003087]">Pal</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {step > 1 ? (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="back-button"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                ) : (
                  <Link to="/shop" className="btn-secondary flex items-center gap-2" data-testid="back-to-shop">
                    <ChevronLeft className="w-4 h-4" />
                    Back to Shop
                  </Link>
                )}
                
                <button
                  onClick={handleNext}
                  disabled={loading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="continue-button"
                >
                  {loading ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      {step === 3 ? "Place Order" : "Continue"}
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="glass-card p-6 sticky top-28" data-testid="order-summary">
                <h3 className="text-lg font-light text-[#F5F5F0] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Order Summary
                </h3>
                
                <div className="space-y-4 max-h-64 overflow-y-auto mb-6">
                  {cart.items?.map((item, idx) => (
                    <div key={`${item.product_id}-${idx}`} className="flex gap-3">
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden">
                        <img src={resolveImageUrl(item.product_image_url)} alt={item.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#F5F5F0] truncate">{item.title}</p>
                        {item.selected_gem_variant && (
                          <p className="text-xs text-primary">{item.selected_gem_variant}</p>
                        )}
                        <p className="text-xs text-[#71717A]">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-sm text-[#F5F5F0]">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-[#A1A1AA]">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-[#A1A1AA]">
                    <span>Tax (8%)</span>
                    <span>{formatPrice(tax)}</span>
                  </div>
                  <div className="flex justify-between text-[#A1A1AA]">
                    <span>Shipping</span>
                    <span>{formatPrice(shippingCost)}</span>
                  </div>
                  <div className="flex justify-between text-[#F5F5F0] text-lg pt-2 border-t border-white/10">
                    <span>Total</span>
                    <span className="text-primary" data-testid="checkout-total">{formatPrice(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
